import { Platform } from "react-native";
import * as Device from "expo-device";

/**
 * プラットフォーム別デバイス情報
 */
export interface DeviceInfo {
  osVersion: string | null;
  modelName: string | null;
  isDevice: boolean;
  osName?: string | null;
  brand?: string | null;
}

/**
 * プラットフォーム別デバイス情報取得
 * Web環境: Web標準API（navigator.userAgent等）を使用
 * iOS/Android環境: expo-deviceを使用（既存動作維持）
 */
export function getDeviceInfo(): DeviceInfo {
  if (Platform.OS === "web") {
    // Web標準API使用
    return {
      osVersion: getWebOSVersion(),
      modelName: getWebBrowserName(),
      isDevice: true,
      osName: getWebOSName(),
      brand: getWebBrand(),
    };
  }

  // iOS/Android: 既存のexpo-device使用（影響なし）
  return {
    osVersion: Device.osVersion,
    modelName: Device.modelName,
    isDevice: Device.isDevice,
    osName: Device.osName,
    brand: Device.brand,
  };
}

/**
 * Web標準APIでOS名を取得
 */
function getWebOSName(): string | null {
  if (typeof navigator === "undefined") return null;

  const ua = navigator.userAgent;

  if (ua.includes("iPhone") || ua.includes("iPad") || ua.includes("iPod")) {
    return "iOS";
  }
  if (ua.includes("Android")) {
    return "Android";
  }
  if (ua.includes("Windows")) {
    return "Windows";
  }
  if (ua.includes("Mac OS X")) {
    return "macOS";
  }
  if (ua.includes("Linux")) {
    return "Linux";
  }

  return null;
}

/**
 * Web標準APIでOSバージョンを取得
 */
function getWebOSVersion(): string | null {
  if (typeof navigator === "undefined") return null;

  const ua = navigator.userAgent;

  // iOS (例: "OS 15_7_9" → "15.7.9")
  const iosMatch = ua.match(/OS (\d+)_(\d+)_?(\d+)?/);
  if (iosMatch !== null) {
    return iosMatch.slice(1, 4).filter(Boolean).join(".");
  }

  // Android (例: "Android 13" → "13")
  const androidMatch = ua.match(/Android (\d+(?:\.\d+)*)/);
  if (androidMatch !== null) {
    return androidMatch[1];
  }

  // Windows (例: "Windows NT 10.0" → "10.0")
  const windowsMatch = ua.match(/Windows NT (\d+\.\d+)/);
  if (windowsMatch !== null) {
    return windowsMatch[1];
  }

  // macOS (例: "Mac OS X 10_15_7" → "10.15.7")
  const macMatch = ua.match(/Mac OS X (\d+[._]\d+(?:[._]\d+)?)/);
  if (macMatch !== null) {
    return macMatch[1].replace(/_/g, ".");
  }

  return null;
}

/**
 * Web標準APIでブラウザ名を取得
 */
function getWebBrowserName(): string | null {
  if (typeof navigator === "undefined") return null;

  const ua = navigator.userAgent;

  // Edge (Chromium版)
  if (ua.includes("Edg/")) return "Edge";
  // Chrome
  if (ua.includes("Chrome") && !ua.includes("Edge")) return "Chrome";
  // Safari
  if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
  // Firefox
  if (ua.includes("Firefox")) return "Firefox";
  // Opera
  if (ua.includes("Opera") || ua.includes("OPR")) return "Opera";

  return "Unknown Browser";
}

/**
 * Web標準APIでブランド/デバイス製造元を取得
 */
function getWebBrand(): string | null {
  if (typeof navigator === "undefined") return null;

  const ua = navigator.userAgent;

  // Apple製品
  if (ua.includes("iPhone") || ua.includes("iPad") || ua.includes("iPod")) {
    return "Apple";
  }
  if (ua.includes("Mac OS X")) {
    return "Apple";
  }
  // Samsung
  if (ua.includes("Samsung")) {
    return "Samsung";
  }
  // Google Pixel
  if (ua.includes("Pixel")) {
    return "Google";
  }
  // その他のAndroid
  if (ua.includes("Android")) {
    return "Android";
  }
  // Windows
  if (ua.includes("Windows")) {
    return "Microsoft";
  }

  return null;
}

/**
 * デバイス情報の個別取得用関数
 */
export const DeviceUtils = {
  get osVersion() {
    return getDeviceInfo().osVersion;
  },
  get modelName() {
    return getDeviceInfo().modelName;
  },
  get isDevice() {
    return getDeviceInfo().isDevice;
  },
  get osName() {
    return getDeviceInfo().osName;
  },
  get brand() {
    return getDeviceInfo().brand;
  },
};
